import { signal } from "@preact/signals";
import * as iOSBridge from "iOSBridge";
import * as _ from "underscore";

type DeviceTypeMap = { [key: string]: () => boolean };

const browserTypes: DeviceTypeMap = {
  chrome: () => {
    return window.chrome && /chrome/i.test(navigator.userAgent);
  },
  safari: () => {
    return (
      /Safari/.test(navigator.userAgent) && !/Chrome/.test(navigator.userAgent)
    );
  },
  firefox: () => /Firefox\//.test(navigator.userAgent),
  ie: () => /Windows.*Trident/.test(navigator.userAgent),
};

/**
 * Identify the kind of machine, can be either OS or more specific device type (e.g. iPhone / iPad)
 */
/**
 * iPadOS 13+ Safari requests desktop sites by default, presenting a
 * `Macintosh` user agent with no `iPad` token. The only reliable way to tell
 * such an iPad apart from a real Mac is the touch screen: Macs report
 * `maxTouchPoints === 0` (a trackpad isn't a touch screen), whereas an iPad
 * reports several. See https://stackoverflow.com/a/57838385.
 */
const isDesktopClassiPad = () =>
  /Macintosh/.test(navigator.userAgent) && navigator.maxTouchPoints > 1;

const platformTypes: DeviceTypeMap = {
  iPhone: () => /iPhone/.test(navigator.userAgent),
  iPad: () => /iPad/.test(navigator.userAgent) || isDesktopClassiPad(),
  android: () => /android/i.test(navigator.userAgent),
  // NB: a desktop-class iPad also matches here (its UA contains "Mac"), which
  // is intentional — `mac` is used to pick the Cmd-key modifier for keyboard
  // shortcuts, and iPad keyboards use Cmd too. Routing that needs to treat the
  // iPad as iOS (e.g. the Web Reader page) checks `iOS` first.
  mac: () => /Mac/.test(navigator.userAgent),
  windows: () => /Win/.test(navigator.userAgent),
  linux: () => /Linux/.test(navigator.userAgent),
};

const allDeviceTypes: DeviceTypeMap = {
  ...browserTypes,
  ...platformTypes,
  iOS: () => is("iPhone") || is("iPad") || /iPod/.test(navigator.userAgent),
  mobile: () => {
    return is("iOS") || is("android");
  },
};

export const is = function (deviceName: string): boolean {
  const deviceChecker = allDeviceTypes[deviceName];

  if (!deviceChecker) {
    throw Error(`Device "${deviceName}" not recognised`);
  }

  return deviceChecker();
};

export const info = function () {
  const result: Record<keyof DeviceTypeMap, boolean> = {};
  _.each(_.keys(allDeviceTypes), function (deviceName) {
    if (is(deviceName)) {
      result[deviceName] = true;
    }
  });
  return result;
};

/**
 * Upper bound of each viewport tier, matching the media query constants in
 * css/_constants.scss. Asking the browser to evaluate the same `max-width`
 * query the stylesheet uses keeps JS and CSS in agreement even at the
 * fractional viewport widths produced by browser zoom, which `innerWidth` and
 * `clientWidth` round differently.
 */
const tierMaxWidths = { mobile: 546, tablet: 754, mediumDesktop: 850 } as const;

type Tier = keyof typeof tierMaxWidths;

const isWidthAtMost = (maxWidth: number) =>
  window.matchMedia(`(max-width: ${maxWidth}px)`).matches;

export const isSmall = () => isWidthAtMost(tierMaxWidths.mobile);

const smallQuery = window.matchMedia(`(max-width: ${tierMaxWidths.mobile}px)`);

/**
 * isSmall(), for components to read during render: it changes as the window
 * is resized or the phone rotated. (The reader's bottom sheet starts at a
 * wider width of its own: ReaderPageView's bottomSheetThreshold.)
 */
export const smallViewport = signal(smallQuery.matches);
const onSmallQueryChange = (event: MediaQueryListEvent) => {
  smallViewport.value = event.matches;
};
// Safari before 14 (and Kindles) have only the older addListener
if ("addEventListener" in smallQuery) {
  smallQuery.addEventListener("change", onSmallQueryChange);
} else {
  (smallQuery as MediaQueryList).addListener(onSmallQueryChange);
}

export const getSizeCategory = (): Tier | "desktop" => {
  const tiers = Object.keys(tierMaxWidths) as Tier[];
  return tiers.find((tier) => isWidthAtMost(tierMaxWidths[tier])) ?? "desktop";
};

/** From https://stackoverflow.com/a/4819886 */
export const isTouchDevice = () => {
  return (
    "ontouchstart" in window ||
    navigator.maxTouchPoints > 0 ||
    navigator["msMaxTouchPoints"] > 0
  );
};

/**
 * Running inside the Readlang iOS app, whose web view injects its bridge
 * (iOSBridge.ts) at document start
 */
export const isIOSApp = () => iOSBridge.get() !== undefined;

/**
 * Returns `true` if running as a home-screen app on iOS or Android.
 */
export const isStandalone = _.memoize(() => {
  // on iOS Safari
  if (window.navigator?.["standalone"]) {
    return true;
  }

  // on Android Chrome
  if (window.matchMedia?.("(display-mode: standalone)")?.matches) {
    return true;
  }

  return false;
});

export const deviceString = () => {
  /** The first platform which matches in platformTypes */
  let platformType: string;

  for (const platformName in platformTypes) {
    if (platformTypes[platformName]()) {
      platformType = platformName;
      break;
    }
  }

  /** The iOS app, or the first browser which matches in browserTypes */
  const browserType = isIOSApp()
    ? "Readlang app"
    : Object.keys(browserTypes).find((browserName) =>
        browserTypes[browserName]()
      );

  const outputList = [platformType, browserType].filter(
    (typeString) => typeString
  );

  return outputList.length > 0 ? outputList.join(", ") : "unknown";
};
